import { supabase } from '../supabase';

import { baseURL } from '../composables/environment';
import { getApiErrorMessage } from '../composables/apiErrors.mjs';

export default class ApiService {
  // Helper to get auth headers
  async _getAuthHeaders(includeContentType = true) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not authenticated");
    return {
      ...(includeContentType ? { 'Content-Type': 'application/json' } : {}),
      'Authorization': `Bearer ${session.access_token}`
    };
  }

  // Helper for fetch requests
  async _fetch(url, options) {
    const response = await fetch(url, options);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
      const error = new Error(getApiErrorMessage(errorData, response.status));
      error.status = response.status;
      error.data = errorData;
      throw error;
    }
    if (response.status === 204) {
      return; // No content
    }
    return response.json();
  }

  // Members
  async getMembers() {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/members`, { headers });
  }
  
  async createMember(memberData) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/members`, {
      method: 'POST',
      headers,
      body: JSON.stringify(memberData)
    });
  }

  async previewMemberImport(file) {
    const headers = await this._getAuthHeaders(false);
    const formData = new FormData();
    formData.append('file', file);
    return this._fetch(`${baseURL}/api/members/import/preview`, {
      method: 'POST',
      headers,
      body: formData
    });
  }
  
  async updateMember(memberData) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/members/${memberData.member_id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(memberData)
    });
  }
  
  async deleteMember(memberId) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/members/${memberId}`, {
      method: 'DELETE',
      headers
    });
  }
  
  async getUnitAddresses() {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/units/addresses`, { headers });
  }

  async setMemberPrimary(memberId, isPrimary) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/members/${memberId}/set-primary`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ is_primary: isPrimary })
    });
  }

  // Dues Products
  async getDuesProducts() {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/dues-products`, { headers });
  }

  async createDuesProduct(productData) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/dues-products`, {
      method: 'POST',
      headers,
      body: JSON.stringify(productData)
    });
  }

  async updateDuesProduct(productData) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/dues-products/${productData.dues_product_id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(productData)
    });
  }

  async deleteDuesProduct(productId) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/dues-products/${productId}`, {
      method: 'DELETE',
      headers
    });
  }

  // Dues Prices
  async getDuesPrices(productId) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/dues-products/${productId}/prices`, { headers });
  }

  async createDuesPrice(productId, priceData) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/dues-products/${productId}/prices`, {
      method: 'POST',
      headers,
      body: JSON.stringify(priceData)
    });
  }

  // Subscriptions
  async getSubscriptions() {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/subscriptions`, { headers });
  }

  async createSubscription(subscriptionData) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/subscriptions`, {
      method: 'POST',
      headers,
      body: JSON.stringify(subscriptionData)
    });
  }

  async cancelSubscription(subscriptionId) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/subscriptions/${subscriptionId}`, {
      method: 'DELETE',
      headers
    });
  }

  async toggleAutopay(memberId, enabled) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/subscriptions/autopay`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ member_id: memberId, enabled })
    });
  }

  async startBilling(data) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/subscriptions/start-billing`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
  }

  // Unbilled Units
  async getUnbilledUnits() {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/units/unbilled`, { headers });
  }
  
  // Invoices
  async getInvoices() {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/invoices`, { headers });
  }
  
  async createInvoice(invoiceData) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/invoices`, {
      method: 'POST',
      headers,
      body: JSON.stringify(invoiceData)
    });
  }
  
  async voidInvoice(invoiceId) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/invoices/void`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ stripe_invoice_id: invoiceId })
    });
  }

  async sendInvoice(invoiceId) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/invoices/send`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ stripe_invoice_id: invoiceId })
    });
  }

  async sendReminder(invoiceId) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/invoices/reminder`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ stripe_invoice_id: invoiceId })
    });
  }

  async markInvoicePaid(stripeInvoiceId) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/invoices/mark-paid`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ stripe_invoice_id: stripeInvoiceId })
    });
  }
  
  // Connect Account
  async setupConnectAccount() {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/stripe/connect/account`, {
      method: 'POST',
      headers
    });
  }
  
  async getConnectStatus() {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/stripe/connect/status`, { headers });
  }

  async updateConnectAccountName() {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/stripe/connect/name`, {
      method: 'PUT',
      headers
    });
  }
  
  // Customer Portal Session
  async createCustomerPortalSession() {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/stripe/customer-portal/session`, {
      method: 'POST',
      headers
    });
  }
  
  // Create Connect Account Session for Notification Banner
  async createConnectAccountSession() {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/stripe/connect/session`, {
      method: 'POST',
      headers
    });
  }
  
  // Create Connect Account Login Link for Dashboard Access
  async createConnectLoginLink() {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/stripe/connect/login-link`, {
      method: 'POST',
      headers
    });
  }

  // Support
  async contactSupport({ subject, message }) {
    const headers = await this._getAuthHeaders();
    return this._fetch(`${baseURL}/api/support/contact`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ subject, message, app_version: __APP_VERSION__ })
    });
  }
}
